import React from "react";
import { Box } from "@mui/material";
import { dtGreen } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { TrackingSession, TrackingPosition } from "../../../../shared/tracking/trackingSlice";
import ActiveServiceCard from "../ActiveServiceCard";
import LivePulseDot from "../LivePulseDot";

interface TabletTrackingLayoutProps {
  activeSessions: TrackingSession[];
  positions: Record<string, TrackingPosition>;
  selectedSessionId: string | null;
  onSessionClick: (session: TrackingSession) => void;
  mapElement: React.ReactNode;
  connectionBanner: React.ReactNode;
}

const TabletTrackingLayout: React.FC<TabletTrackingLayoutProps> = ({
  activeSessions,
  positions,
  selectedSessionId,
  onSessionClick,
  mapElement,
  connectionBanner,
}) => {
  const { translate } = useTranslation(["serviceTracking"]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 120px)",
        backgroundColor: dtGreen.pageBg,
        overflow: "hidden",
      }}
    >
      {/* Map area: 60% */}
      <Box sx={{ height: "60%", width: "100%" }}>
        {mapElement}
      </Box>

      {/* Horizontal scroll cards: 40% */}
      <Box
        sx={{
          height: "40%",
          backgroundColor: dtGreen.cardBg,
          borderTop: `1px solid ${dtGreen.cardBorder}`,
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontFamily: dtGreen.font,
            fontSize: "14px",
            fontWeight: 600,
            color: dtGreen.textPrimary,
            flexShrink: 0,
          }}
        >
          <LivePulseDot size={8} color={dtGreen.green500} />
          {activeSessions.length} {translate("serviceTracking:activeServices")}
        </Box>

        {/* Connection banner */}
        {connectionBanner}

        <Box
          sx={{
            display: "flex",
            gap: "12px",
            overflowX: "auto",
            flex: 1,
            pb: 1,
            "&::-webkit-scrollbar": { height: 4 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: dtGreen.textMuted,
              borderRadius: 2,
            },
          }}
        >
          {activeSessions.map((session) => (
            <Box
              key={session.id}
              sx={{ minWidth: 280, maxWidth: 320, flexShrink: 0 }}
            >
              <ActiveServiceCard
                session={session}
                position={positions[session.id] || null}
                isSelected={session.id === selectedSessionId}
                onClick={onSessionClick}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(TabletTrackingLayout);
